import { Component } from '@theme/component';

const revealSelector = '.media-with-content__content, .section-resource-list__header, .collection-card';
const productRevealSelector = '[data-testid="product-list"] slideshow-slides > slideshow-slide.resource-list__slide';

class OakwellMotionController extends Component {
  #abort;
  #observer;
  #rowObserver;
  #productRows = new Map();
  #mutations;
  #frame;
  #main;
  #seen = new WeakSet();
  #pending = new Set();
  #active = new Set();
  #motion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();
    this.#abort?.abort();
    this.#abort = new AbortController();
    const options = { signal: this.#abort.signal };
    this.#motion.addEventListener('change', this.#start, options);
    window.addEventListener('pageshow', this.#onPageShow, options);
    window.addEventListener('pagehide', this.#stop, options);
    document.addEventListener('focusin', this.#onFocus, options);
    document.addEventListener('animationend', this.#onAnimationEnd, options);
    this.#start();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#stop();
  }

  #onPageShow = (event) => {
    if (event.persisted) this.#start();
  };

  #start = () => {
    this.#stop();
    this.#main = document.getElementById('MainContent');
    if (!this.#main || this.#motion.matches || !('IntersectionObserver' in window)) return;

    try {
      this.#observer = new IntersectionObserver((entries) => {
        const productRows = new Map();
        for (const entry of entries) {
          if (!entry.isIntersecting || !this.#pending.has(entry.target)) continue;
          if (!entry.target.classList.contains('oakwell-reveal--product')) {
            this.#reveal(entry.target);
            continue;
          }
          const row = entry.target.parentElement;
          if (!productRows.has(row)) productRows.set(row, []);
          productRows.get(row).push(entry);
        }
        for (const row of productRows.values()) {
          row.sort((a, b) => a.boundingClientRect.left - b.boundingClientRect.left);
          row.forEach((entry, index) => {
            entry.target.style.setProperty('--oakwell-reveal-delay', `${Math.min(index * 75, 300)}ms`);
            this.#reveal(entry.target);
          });
        }
      }, { rootMargin: '0px 0px -24px 0px', threshold: 0.06 });

      this.#rowObserver = new IntersectionObserver((entries) => {
        for (const entry of entries) {
          if (!this.#productRows.has(entry.target)) continue;
          if (entry.isIntersecting) {
            this.#productRows.set(entry.target, true);
            continue;
          }
          const above = entry.boundingClientRect.bottom <= (entry.rootBounds?.top ?? 0);
          const below = entry.boundingClientRect.top >= (entry.rootBounds?.bottom ?? document.documentElement.clientHeight);
          if (this.#productRows.get(entry.target) && (above || below)) {
            this.#productRows.set(entry.target, false);
            this.#armProductRow(entry.target);
          }
        }
      }, { threshold: 0 });

      this.#collect();
      this.#mutations = new MutationObserver((records) => {
        for (const record of records) {
          for (const removed of record.removedNodes) {
            if (!(removed instanceof Element)) continue;
            for (const target of new Set([...this.#pending, ...this.#active])) {
              if (removed === target || removed.contains(target)) this.#reveal(target, true);
            }
            for (const row of this.#productRows.keys()) {
              if (removed === row || removed.contains(row)) {
                this.#rowObserver?.unobserve(row);
                this.#productRows.delete(row);
                for (const target of row.children) this.#seen.delete(target);
              }
            }
          }
        }
        if (!records.some(record => record.addedNodes.length) || this.#frame) return;
        this.#frame = requestAnimationFrame(() => {
          this.#frame = undefined;
          this.#collect();
        });
      });
      this.#mutations.observe(this.#main, { childList: true, subtree: true });
    } catch {
      this.#stop();
    }
  };

  #collect() {
    if (!this.#main || !this.#observer) return;
    const viewportHeight = document.documentElement.clientHeight;
    const isHomepage = this.#main.dataset.template?.split('.')[0] === 'index';
    if (isHomepage) {
      for (const target of this.#main.querySelectorAll(productRevealSelector)) {
        const row = target.parentElement;
        if (this.#productRows.has(row)) continue;
        this.#productRows.set(row, false);
        this.#rowObserver?.observe(row);
      }
    }
    const selectors = isHomepage ? `${revealSelector}, ${productRevealSelector}` : revealSelector;
    const candidates = Array.from(this.#main.querySelectorAll(selectors))
      .filter(target => !this.#seen.has(target))
      .map(target => ({ target, bounds: target.getBoundingClientRect() }));

    let index = 0;
    for (const { target, bounds } of candidates) {
      this.#seen.add(target);
      if (!bounds.width || !bounds.height || bounds.top < viewportHeight || target.contains(document.activeElement)) continue;
      target.style.setProperty('--oakwell-reveal-delay', target.matches('.collection-card') ? `${Math.min(index++ % 4 * 35, 105)}ms` : '0ms');
      this.#observer.observe(target);
      target.classList.add('oakwell-reveal', 'oakwell-reveal--pending');
      if (isHomepage && target.matches(productRevealSelector)) target.classList.add('oakwell-reveal--product');
      this.#pending.add(target);
    }
  }

  #armProductRow(row) {
    if (row.contains(document.activeElement)) return;
    for (const target of row.children) {
      if (!target.matches('slideshow-slide.resource-list__slide')) continue;
      this.#reveal(target, true);
      target.style.setProperty('--oakwell-reveal-delay', '0ms');
      target.classList.add('oakwell-reveal', 'oakwell-reveal--pending', 'oakwell-reveal--product');
      this.#pending.add(target);
      this.#observer?.observe(target);
    }
  }

  #onFocus = (event) => {
    if (!(event.target instanceof Element)) return;
    const target = event.target.closest('.oakwell-reveal--pending, .oakwell-reveal--entering');
    if (target) this.#reveal(target, true);
  };

  #onAnimationEnd = (event) => {
    if (event.animationName === 'oakwell-product-arrive' && event.target instanceof Element && this.#active.has(event.target)) {
      this.#reveal(event.target, true);
    }
  };

  #reveal(target, immediate = false) {
    if (immediate) {
      target.classList.remove('oakwell-reveal', 'oakwell-reveal--product', 'oakwell-reveal--entering');
      this.#active.delete(target);
    } else if (target.classList.contains('oakwell-reveal--product')) {
      target.classList.add('oakwell-reveal--entering');
      this.#active.add(target);
    }
    target.classList.remove('oakwell-reveal--pending');
    this.#observer?.unobserve(target);
    this.#pending.delete(target);
  }

  #stop = () => {
    this.#observer?.disconnect();
    this.#rowObserver?.disconnect();
    this.#mutations?.disconnect();
    if (this.#frame) cancelAnimationFrame(this.#frame);
    this.#frame = undefined;
    for (const target of new Set([...this.#pending, ...this.#active])) this.#reveal(target, true);
    for (const row of this.#productRows.keys()) {
      for (const target of row.children) this.#seen.delete(target);
    }
    this.#productRows.clear();
    this.#observer = undefined;
    this.#rowObserver = undefined;
    this.#mutations = undefined;
  };
}

if (!customElements.get('oakwell-motion-controller')) {
  customElements.define('oakwell-motion-controller', OakwellMotionController);
}

